import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { isDeepEqual } from '@/utility/comparators';
import { deserializeObjectArray } from '@/hooks/useQueryParams';
import type { BaseFiltersContextType } from '@/components/BaseFilters/types';

/**
 * Context for managing filter state across BaseFilters and child components
 */
const FilterContext = createContext<BaseFiltersContextType | undefined>(
  undefined
);

/**
 * Query params arrive URL-serialized (an object array comes back as a list of
 * `label:value` strings), so they're normalised once on the way in.
 */
function deserializeState(
  state: Record<string, unknown>
): Record<string, unknown> {
  const deserialized = { ...state };
  Object.entries(state).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      deserialized[key] = deserializeObjectArray(value, ['label', 'value']);
    }
  });
  return deserialized;
}

/**
 * Provider component that manages filter state
 */
export function FilterProvider({
  children,
  state,
  defaultState,
  onApply,
  searchFilterKey,
}: {
  children: React.ReactNode;
  state: Record<string, unknown>;
  defaultState: Record<string, unknown>;
  onApply: (filterState: Record<string, unknown>) => void;
  searchFilterKey?: string;
}) {
  const [filters, setFilters] = useState<Record<string, unknown>>(() =>
    deserializeState(state)
  );
  const [isOpen, setIsOpen] = useState(false);
  const [backup, setBackup] = useState<Record<string, unknown>>({});
  const [filterHeadings, setFilterHeadings] = useState<Record<string, string>>(
    {}
  );
  // Some filter keys are driven from outside this provider — a `SearchBar`
  // sitting next to the panel, a reset triggered by another control, browser
  // navigation. Without adopting those changes the panel keeps showing the
  // state it mounted with, so the search term never appears as a chip. Only
  // the keys that actually changed upstream are overwritten, so a pending
  // in-panel edit of an unrelated filter survives.
  const lastExternalState = useRef<Record<string, unknown>>(
    deserializeState(state)
  );
  useEffect(() => {
    const incoming = deserializeState(state);
    const previous = lastExternalState.current;
    if (isDeepEqual(incoming, previous)) return;
    lastExternalState.current = incoming;

    const changed: Record<string, unknown> = {};
    Object.keys({ ...previous, ...incoming }).forEach((key) => {
      if (!isDeepEqual(incoming[key], previous[key])) {
        changed[key] = incoming[key];
      }
    });
    if (Object.keys(changed).length === 0) return;

    setFilters((prev) => ({ ...prev, ...changed }));
    setBackup((prev) => ({ ...prev, ...changed }));
  }, [state]);

  // Calculate badge count (number of active filters)
  const badgeCount = useCallback(() => {
    const excludedFilterKeys = new Set(['page', 'limit', 'search', 'type']);
    if (searchFilterKey) excludedFilterKeys.add(searchFilterKey);
    let count = 0;

    Object.entries(filters).forEach(([key, value]) => {
      // Exclude pagination and search filters from badge count
      if (excludedFilterKeys.has(key)) {
        return;
      }

      const defaultValue = defaultState[key];

      if (isDeepEqual(value, defaultValue)) return;

      // For arrays: count the number of selected items
      if (Array.isArray(value)) {
        count += value.length || 0;
      } else {
        // For primitives and objects: count as 1
        count += 1;
      }
    });

    return count;
  }, [filters, defaultState, searchFilterKey]);

  const updateFilter = useCallback((name: string, value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const openPanel = useCallback(() => {
    setBackup(filters);
    setIsOpen(true);
  }, [filters]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const applyFilters = useCallback(() => {
    onApply(filters);
    setBackup(filters);
    closePanel();
  }, [filters, onApply, closePanel]);

  const cancelFilters = useCallback(() => {
    setFilters(backup);
    closePanel();
  }, [backup, closePanel]);

  const clearAllFilters = useCallback(() => {
    setFilters(defaultState);
    setBackup(defaultState);
    onApply(defaultState);
  }, [defaultState, onApply]);

  const resetFilter = useCallback(
    (name: string) => {
      const next = { ...filters, [name]: defaultState[name] };
      setFilters(next);
      setBackup(next);
      onApply(next);
    },
    [filters, defaultState, onApply]
  );

  const registerFilterHeading = useCallback((name: string, heading: string) => {
    setFilterHeadings((prev) =>
      prev[name] === heading ? prev : { ...prev, [name]: heading }
    );
  }, []);

  const value: BaseFiltersContextType = {
    filters,
    defaultState,
    isOpen,
    badgeCount: badgeCount(),
    filterHeadings,
    updateFilter,
    openPanel,
    closePanel,
    applyFilters,
    cancelFilters,
    clearAllFilters,
    resetFilter,
    registerFilterHeading,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

/**
 * Hook to access filter context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useFilterContext(): BaseFiltersContextType {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error(
      'useFilterContext must be used within a FilterProvider (BaseFilters)'
    );
  }
  return context;
}
